
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { UploadFile } from '@/integrations/Core';
import { Document, User } from '@/entities/all';
import {
    UploadCloud,
    FileText,
    Trash2,
    Download,
    File,
    CheckCircle,
    FileImage,
    FileSpreadsheet
} from 'lucide-react';

const categoryConfig = {
    technical_specs: { label: 'Spécifications techniques', color: 'bg-blue-100 text-blue-800' },
    contracts: { label: 'Contrats', color: 'bg-purple-100 text-purple-800' },
    approvals: { label: 'Approbations', color: 'bg-green-100 text-green-800' },
    reports: { label: 'Rapports', color: 'bg-yellow-100 text-yellow-800' },
    photos: { label: 'Photos', color: 'bg-orange-100 text-orange-800' },
    other: { label: 'Autres', color: 'bg-gray-100 text-gray-800' },
};

const getFileIcon = (fileName) => {
    const extension = fileName?.split('.').pop().toLowerCase();
    switch (extension) {
        case 'pdf': return <FileText className="w-8 h-8 text-red-500" />;
        case 'doc':
        case 'docx': return <FileText className="w-8 h-8 text-blue-500" />;
        case 'xls':
        case 'xlsx': return <FileSpreadsheet className="w-8 h-8 text-green-500" />;
        case 'jpg':
        case 'jpeg':
        case 'png': return <FileImage className="w-8 h-8 text-orange-500" />;
        default: return <File className="w-8 h-8 text-gray-500" />;
    }
};

// @ts-nocheck
export default function DocumentsTab({ project }) {
    const [documents, setDocuments] = useState([]);
    const [uploadingFiles, setUploadingFiles] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [currentUser, setCurrentUser] = useState(null);
    const [isDragActive, setIsDragActive] = useState(false);
    const inputRef = useRef(null);

    const loadDocuments = useCallback(async () => {
        if (!project) return;
        setIsLoading(true);
        try {
            const docs = await Document.filter({ project_id: project.id });
            setDocuments(docs);
        } catch (error) {
            console.error("Failed to load documents:", error);
        } finally {
            setIsLoading(false);
        }
    }, [project]);

    useEffect(() => {
        loadDocuments();
        User.me().then(setCurrentUser).catch(() => console.log("User not logged in"));
    }, [loadDocuments]);

    const handleFileUpload = useCallback(async (files) => {
        const acceptedFiles = Array.from(files);
        const newUploadingFiles = acceptedFiles.map(file => ({
            name: file.name,
            progress: 0,
            error: null,
            file,
        }));
        setUploadingFiles(prev => [...prev, ...newUploadingFiles]);

        for (const uploadingFile of newUploadingFiles) {
            try {
                setUploadingFiles(prev => prev.map(f => f.name === uploadingFile.name ? { ...f, progress: 20 } : f));

                const { file_url } = await UploadFile({ file: uploadingFile.file });

                setUploadingFiles(prev => prev.map(f => f.name === uploadingFile.name ? { ...f, progress: 80 } : f));

                await Document.create({
                    project_id: project.id,
                    name: uploadingFile.name,
                    category: 'other', // Default category
                    file_url,
                    uploaded_by: currentUser?.full_name || 'Inconnu',
                });

                setUploadingFiles(prev => prev.map(f => f.name === uploadingFile.name ? { ...f, progress: 100 } : f));

            } catch (error) {
                setUploadingFiles(prev => prev.map(f => f.name === uploadingFile.name ? { ...f, error: 'Échec de l\'upload' } : f));
                console.error("Upload failed for:", uploadingFile.name, error);
            }
        }

        await loadDocuments();
        setTimeout(() => setUploadingFiles([]), 3000);
    }, [project, currentUser, loadDocuments]);

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setIsDragActive(true);
        } else if (e.type === "dragleave") {
            setIsDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileUpload(e.dataTransfer.files);
        }
    };

    const handleChange = (e) => {
        e.preventDefault();
        if (e.target.files && e.target.files[0]) {
            handleFileUpload(e.target.files);
        }
    };

    const onZoneClick = () => {
        inputRef.current.click();
    };

    const handleDelete = async (docId) => {
        if(window.confirm("Êtes-vous sûr de vouloir supprimer ce document ?")) {
            try {
                await Document.delete(docId);
                loadDocuments();
            } catch (error) {
                console.error("Failed to delete document:", error);
            }
        }
    };

    return (
        <div className="space-y-6">
            <Card className="border-0 shadow-lg">
                <CardHeader>
                    <CardTitle>Zone de dépôt</CardTitle>
                </CardHeader>
                <CardContent>
                    <div
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        onClick={onZoneClick}
                        className={`p-10 border-2 border-dashed rounded-lg text-center cursor-pointer transition-colors ${isDragActive ? 'border-teal-600 bg-teal-50' : 'border-gray-300 hover:border-teal-500'}`}
                    >
                        <input ref={inputRef} type="file" multiple onChange={handleChange} className="hidden" />
                        <div className="flex flex-col items-center gap-2">
                            <UploadCloud className="w-12 h-12 text-gray-400" />
                            <p className="text-gray-600">
                                {isDragActive ? 'Déposez les fichiers ici...' : 'Glissez-déposez des fichiers ici, ou cliquez pour sélectionner'}
                            </p>
                            <p className="text-xs text-gray-500">PDF, DOCX, XLSX, JPG, PNG (Max 25MB)</p>
                        </div>
                    </div>
                    {uploadingFiles.length > 0 && (
                        <div className="mt-4 space-y-2">
                            {uploadingFiles.map((file, index) => (
                                <div key={index} className="flex items-center gap-4 p-2 border rounded-lg">
                                    <File className="w-5 h-5" />
                                    <div className="flex-1">
                                        <p className="text-sm font-medium">{file.name}</p>
                                        <Progress value={file.progress} className="h-1.5 mt-1" />
                                    </div>
                                    {file.error && <span className="text-xs text-red-500">{file.error}</span>}
                                    {file.progress === 100 && <CheckCircle className="w-5 h-5 text-green-500" />}
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
                <CardHeader>
                    <CardTitle>Documents du projet</CardTitle>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-[100px]">Type</TableHead>
                                <TableHead>Nom du fichier</TableHead>
                                <TableHead>Catégorie</TableHead>
                                <TableHead>Date d'ajout</TableHead>
                                <TableHead>Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {documents.map((doc) => (
                                <TableRow key={doc.id}>
                                    <TableCell>{getFileIcon(doc.name)}</TableCell>
                                    <TableCell className="font-medium">{doc.name}</TableCell>
                                    <TableCell>
                                        <Badge className={categoryConfig[doc.category]?.color || categoryConfig.other.color}>
                                            {categoryConfig[doc.category]?.label || 'Autre'}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>{new Date(doc.created_date).toLocaleDateString('fr-FR')}</TableCell>
                                    <TableCell className="space-x-2">
                                        <Button variant="outline" size="icon" asChild>
                                            <a href={doc.file_url} target="_blank" rel="noopener noreferrer">
                                                <Download className="h-4 w-4" />
                                            </a>
                                        </Button>
                                        <Button variant="destructive" size="icon" onClick={() => handleDelete(doc.id)}>
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {documents.length === 0 && !isLoading && (
                        <div className="text-center py-10">
                            <p className="text-gray-500">Aucun document trouvé pour ce projet.</p>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
